import { jsPDF } from 'jspdf'
import { storeToRefs } from 'pinia'
import { useChatStore } from '../stores/chatStore'
import type { Message } from '../types/chat/Message'
import { MessageType } from '../types/chat/MessageType'
import { useAppStore } from '../stores/appStore'

function htmlToPlainText(html: string): string {
  if (!html) return ''
  const el = document.createElement('div')
  el.innerHTML = html
  return el.innerText || el.textContent || ''
}

export function useAlternatingBubblesPdf() {
  const chatStore = useChatStore()
  const appStore = useAppStore()
  const { messages } = storeToRefs(chatStore)

  const transcriptMessagePairs = appStore.configuration?.components.supportRequest.messagePairs || 3

  async function buildAlternatingBubblesPdf(): Promise<Blob | null> {
    const cleanedMessages: Message[] = messages.value
      .filter(m => !m.isLoading && !!m.text)
      .map(m => ({
        ...m,
        text: htmlToPlainText(m.text).trim(),
      }))
      .filter(m => m.text.length > 0)

    if (!cleanedMessages.length) {
      console.warn('No messages to export to PDF')
      return null
    }

    const totalPairs = Math.floor(cleanedMessages.length / 2)
    const pairsToUse = Math.min(transcriptMessagePairs, totalPairs)

    const sliceSize = pairsToUse * 2
    const sourceMessages = cleanedMessages.slice(-sliceSize)

    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'p' })
    const margin = 40
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const left = margin
    const right = pageW - margin
    const bottom = pageH - margin

    const bubbleW = Math.floor((right - left) * 0.66)
    const padX = 10
    const padY = 8
    const gapBetweenMsgs = 12

    const capSize = 10
    const bodySize = 12
    const capH = capSize * 1.2
    const lineH = bodySize * 1.3

    const blue = { r: 33, g: 94, b: 248 }
    const gray = { r: 240, g: 240, b: 240 }
    const black = { r: 0, g: 0, b: 0 }
    const white = { r: 255, g: 255, b: 255 }

    let y = margin
    doc.setFont('helvetica', 'normal')

    const addPage = () => {
      doc.addPage()
      y = margin
    }

    sourceMessages.forEach(msg => {
      const raw = msg.text
      if (!raw) return

      const isUser = msg.type === MessageType.User
      const isRight = isUser
      const caption = isUser ? 'User prompt' : 'Agent response'

      const bubbleX = isRight ? right - bubbleW : left
      const align: 'left' | 'right' = isRight ? 'right' : 'left'
      const textX = isRight ? bubbleX + bubbleW - padX : bubbleX + padX

      // caption
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(capSize)
      if (y + capH > bottom) addPage()
      doc.setTextColor(0, 0, 0)
      const capAlignX = isRight ? right : left
      doc.text(caption, capAlignX, y + capH, { align })
      y += capH + 4

      // body
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(bodySize)
      const allLines = doc.splitTextToSize(raw, bubbleW - padX * 2) as string[]
      let i = 0

      while (i < allLines.length) {
        if (y + padY + lineH > bottom) addPage()
        const available = bottom - y
        const linesFit = Math.max(1, Math.floor((available - padY * 2) / lineH))
        const chunk = allLines.slice(i, i + linesFit)

        const rectH = padY * 2 + chunk.length * lineH
        const radius = 8

        if (isRight) {
          doc.setFillColor(blue.r, blue.g, blue.b)
          doc.roundedRect(bubbleX, y, bubbleW, rectH, radius, radius, 'F')
          doc.setTextColor(white.r, white.g, white.b)
        } else {
          doc.setFillColor(gray.r, gray.g, gray.b)
          doc.roundedRect(bubbleX, y, bubbleW, rectH, radius, radius, 'F')
          doc.setTextColor(black.r, black.g, black.b)
        }

        let yy = y + padY
        for (const line of chunk) {
          yy += lineH
          doc.text(line, textX, yy, { align })
        }

        y += rectH
        i += chunk.length
        if (i < allLines.length && y + 4 <= bottom) y += 4
      }

      if (y + gapBetweenMsgs <= bottom) y += gapBetweenMsgs
      else addPage()
    })

    doc.setTextColor(0, 0, 0)
    return doc.output('blob')
  }

  return { buildAlternatingBubblesPdf }
}
